import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "gui-common/redux/store";
import {Button, Header, Input, ProgressBar, Surface, Text, useTheme} from "tmwx-design-web/src";
import {useTranslation} from "react-i18next";
import {isEmpty} from "gui-common/appHelper";
import {usePermissions} from "tmwx-design-common";
import {Template} from "gui-common/redux/data/register";
import {getTemplatesList} from "gui-common/redux/action/efe/registerActions";
import {registerActions} from "gui-common/redux/slice/efe/registerSlice";
import {Box, Chip, Divider, Stack} from "@mui/material";

function ModalRow({template, handleOnClick}: { template: Template, handleOnClick: (template: Template) => void }) {
    const {padding, colors, roundness} = useTheme();
    const {t} = useTranslation();

    return (
        <Box
            onClick={() => handleOnClick(template)}
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(180px, 1fr) 180px auto' },
                gap: 1,
                alignItems: 'center',
                px: padding,
                py: 1,
                borderRadius: `${roundness}px`,
                backgroundColor: colors.elevation.level0,
                border: `1px solid ${colors.elevation.level2}`,
                transition: 'transform 140ms ease, background-color 140ms ease, border-color 140ms ease',
                cursor: 'pointer',
                '&:hover': {
                    backgroundColor: colors.elevation.level1,
                    borderColor: colors.elevation.level3,
                    transform: 'translateY(-1px)',
                }
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Text bold style={{ marginBottom: 0 }}>
                    {template.dteTemplateName}
                </Text>
                <Text secondary variant="helper">
                    {template.dteTemplateTag}
                </Text>
            </Box>

            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Chip
                    size="small"
                    label={template.dteDeviceTypeTag}
                    variant="outlined"
                    sx={{
                        borderColor: colors.elevation.level3,
                        color: colors.onSurface,
                    }}
                />
            </Box>

            <Box sx={{ justifySelf: { xs: 'start', md: 'end' } }}>
                <Button
                    size="small"
                    variant="contained"
                    onPress={() => handleOnClick(template)}
                >
                    {t('select')}
                </Button>
            </Box>
        </Box>
    );
}

const TemplateSearchModal = ({label}: { label: string }) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const { hasRights } = usePermissions()

    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [search, setSearch] = useState<string>('')
    const [selectedDeviceType, setSelectedDeviceType] = useState<string>("ALL");

    const loading = useSelector<RootState, boolean>(state => state.register.loading);
    const templatesList = useSelector<RootState, Template[]>(state => state.register.templatesList);
    const {colors, padding, roundness} = useTheme();

    useEffect(() => {
        if (hasRights(["efe_registers_view"])) {
            dispatch(getTemplatesList());
        }
    }, [dispatch, hasRights]);

    const handleOnClick = (selectedTemplate: Template) => {
        dispatch(registerActions.setSelectedTemplate(selectedTemplate))
        handleClose()
    }

    const handleClose = () => {
        setIsDialogOpen(false);
    };

    const handleDialogOpen = () => {
        setIsDialogOpen(true);
    };

    const deviceTypeOptions = useMemo(() => {
        const order = ["METER", "INVERTER"];
        const types = Array.from(
            new Set(
                templatesList
                    .map((template) => template.dteDeviceTypeTag?.trim())
                    .filter((type): type is string => !isEmpty(type))
                    .map((type) => type.toUpperCase())
            )
        );

        return [
            ...order.filter((type) => types.includes(type)),
            ...types.filter((type) => !order.includes(type)).sort(),
        ];
    }, [templatesList]);

    const filteredTemplates = useMemo(() => {
        const query = search.trim().toLowerCase();
        const selectedType = selectedDeviceType.toUpperCase();

        return templatesList.filter((val) => {
            const matchesType =
                selectedType === "ALL" ||
                val.dteDeviceTypeTag?.trim().toUpperCase() === selectedType;

            if (!matchesType) {
                return false;
            }

            if (isEmpty(query)) {
                return true;
            }

            return (
                val.dteTemplateName?.toLowerCase().includes(query) ||
                val.dteTemplateTag?.toLowerCase().includes(query) ||
                val.dteDeviceTypeTag?.toLowerCase().includes(query)
            );
        });
    }, [search, selectedDeviceType, templatesList]);

    useEffect(() => {
        if (selectedDeviceType !== "ALL" && !deviceTypeOptions.includes(selectedDeviceType)) {
            setSelectedDeviceType("ALL");
        }
    }, [deviceTypeOptions, selectedDeviceType]);

    const getDeviceTypeLabel = (deviceType: string) => {
        switch (deviceType.toUpperCase()) {
            case "METER":
                return t("efe.details.meter");
            case "INVERTER":
                return t("efe.details.inverter");
            default:
                return deviceType;
        }
    };

    const selectedFilterLabel = selectedDeviceType === "ALL"
        ? t("showAll")
        : getDeviceTypeLabel(selectedDeviceType);

    return (
        <Surface style={{ marginBottom: 4, marginLeft: 4, marginRight: 4, width: '100%', flex: 1, minWidth: 1120 }}>
            <Box
                onClick={handleDialogOpen}
                sx={{
                    cursor: 'pointer',
                    position: 'relative',
                    width: '100%',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        borderRadius: `${roundness}px`,
                        pointerEvents: 'none',
                    }
                }}
            >
                <Input
                    style={{ ml: 1, fontWeight: 500, pointerEvents: 'none', width: '100%' }}
                    fullWidth
                    icon={"search"}
                    placeholder={t('efe.efeSearchTemplate')}
                    value={label}
                    onChangeValue={() => {}}
                />
            </Box>

            {isDialogOpen && (
                <Box
                    onClick={handleClose}
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 20000,
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2,
                    }}
                >
                    <Box onClick={(event) => event.stopPropagation()}>
                        <Surface
                            elevation={0}
                            style={{
                                padding,
                                width: 'min(78vw, 1200px)',
                                maxHeight: '90vh',
                                overflow: 'hidden',
                            }}
                        >
                            <Header
                                title={t('efe.templateSearch')}
                                subtitle={t('efe.efeSearchTemplate')}
                                actionRight={<Button iconLeft={'clear'} status='neutral' variant='ghost'
                                                     onPress={handleClose}/>}
                            />

                            <Box style={{ padding }}>
                                <Stack spacing={2}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', md: 'row' },
                                        alignItems: { xs: 'stretch', md: 'center' },
                                        justifyContent: 'space-between',
                                        gap: 1.5,
                                        px: 0.5,
                                    }}
                                >
                                    <Box>
                                        <Text bold style={{ fontSize: 18 }}>
                                            {t('efe.templateSearch')}
                                        </Text>
                                        <Text secondary variant="helper">
                                            {t('efe.efeSearchTemplate')}
                                        </Text>
                                    </Box>

                                    <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-end">
                                        <Chip
                                            size="small"
                                            label={`${selectedFilterLabel} · ${filteredTemplates.length} ${t("searchResultCount")}`}
                                        />
                                        <Chip
                                            size="small"
                                            label={t("showAll")}
                                            clickable
                                            color={selectedDeviceType === "ALL" ? "primary" : "default"}
                                            variant={selectedDeviceType === "ALL" ? "filled" : "outlined"}
                                            onClick={() => setSelectedDeviceType("ALL")}
                                        />
                                        {deviceTypeOptions.map((deviceType) => (
                                            <Chip
                                                key={deviceType}
                                                size="small"
                                                label={getDeviceTypeLabel(deviceType)}
                                                clickable
                                                color={selectedDeviceType === deviceType ? "primary" : "default"}
                                                variant={selectedDeviceType === deviceType ? "filled" : "outlined"}
                                                onClick={() => setSelectedDeviceType(deviceType)}
                                            />
                                        ))}
                                    </Stack>
                                </Box>

                                <Box sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: colors.elevation.level0, pt: 0.25, pb: 0.75 }}>
                                    <Input
                                        style={{ ml: 1, fontWeight: 500 }}
                                        fullWidth
                                        icon={"search"}
                                        placeholder={t('efe.efeSearchTemplate')}
                                        value={search}
                                        onChangeValue={(text) => setSearch(text)}
                                    />
                                </Box>

                                <Divider />

                                <Box sx={{ maxHeight: '52vh', overflowY: 'auto', pr: 0.5 }}>
                                    {loading ? (
                                        <ProgressBar animated rounded={false} width={'100%'} height={6} vertical={false}/>
                                    ) : isEmpty(filteredTemplates) ? (
                                        <Box
                                            sx={{
                                                p: 3,
                                                textAlign: 'center',
                                                borderRadius: `${roundness}px`,
                                                backgroundColor: colors.elevation.level0,
                                                border: `1px dashed ${colors.elevation.level2}`,
                                            }}
                                        >
                                            <Text bold style={{ fontSize: 16 }}>
                                                {isEmpty(search) ? t('noData') : t('noResult')}
                                            </Text>
                                            <Text secondary variant="helper" style={{ marginTop: 6 }}>
                                                {isEmpty(search)
                                                    ? t('efe.efeSearchTemplate')
                                                    : `${search} ${t('filter')}`
                                                }
                                            </Text>
                                        </Box>
                                    ) : (
                                        <Stack spacing={1}>
                                            {filteredTemplates.map((item) => (
                                                <ModalRow
                                                    key={`${item.dteTemplateTag}-${item.dteDeviceTypeTag}`}
                                                    template={item}
                                                    handleOnClick={handleOnClick}
                                                />
                                            ))}
                                        </Stack>
                                    )}
                                </Box>
                                </Stack>
                            </Box>
                        </Surface>
                    </Box>
                </Box>
            )}
        </Surface>
    )
}

export default TemplateSearchModal;
